import { createReducer, on } from '@ngrx/store';
import { InstallationFeaturesState } from './installation-features.state';
import { InstallationFeaturesActions } from './installation-features.actions';
import { EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { Feature } from '../../../projects/client';
import { GatewayActions } from '../gateway/gateway.actions';

export function selectFeatureId(feature: Feature<unknown, unknown>): string {
  return feature.uri;
}

export const featuresAdapter: EntityAdapter<Feature<unknown, unknown>> = createEntityAdapter<
  Feature<unknown, unknown>
>({
  selectId: selectFeatureId,
});

export const initialInstallationFeaturesState: InstallationFeaturesState = featuresAdapter.getInitialState(
  {
    getFeaturesLoading: false,
    getFeaturesForAllGatwewaysLoading: false,
    getFeaturesError: null,
    updateFeatureLoading: false,
    updateFeaturesError: null,
    getFeaturesRequestFinished: false,
    rooms: [],
    addRoom: null,
  }
);

export const installationFeaturesReducer = createReducer(
  initialInstallationFeaturesState,
  on(
    InstallationFeaturesActions.fetchFeatures.attempt,
    (state): InstallationFeaturesState => ({
      ...state,
      getFeaturesLoading: true,
    })
  ),
  on(
    InstallationFeaturesActions.fetchFeatures.success,
    (state, { features }): InstallationFeaturesState =>
      featuresAdapter.upsertMany(features, {
        ...state,
        getFeaturesLoading: false,
        getFeaturesError: null,
      })
  ),
  on(
    InstallationFeaturesActions.fetchFeatures.error,
    (state, { error }): InstallationFeaturesState => ({
      ...state,
      getFeaturesLoading: false,
      getFeaturesError: error,
    })
  ),
  on(
    InstallationFeaturesActions.updateFeature.attempt,
    (state): InstallationFeaturesState => ({
      ...state,
      updateFeatureLoading: true,
    })
  ),
  on(
    InstallationFeaturesActions.updateFeature.success,
    (state): InstallationFeaturesState => ({
      ...state,
      updateFeatureLoading: false,
      updateFeaturesError: null,
    })
  ),
  on(
    InstallationFeaturesActions.updateFeature.error,
    (state, { error }): InstallationFeaturesState => ({
      ...state,
      updateFeatureLoading: false,
      updateFeaturesError: error,
    })
  ),
  on(
    InstallationFeaturesActions.fetchFeaturesForInstallationGateways.attempt,
    (state): InstallationFeaturesState => ({
      ...state,
      getFeaturesForAllGatwewaysLoading: true,
      getFeaturesRequestFinished: false,
    })
  ),
  on(
    InstallationFeaturesActions.fetchFeaturesForInstallationGateways.aborted,
    (state): InstallationFeaturesState => ({
      ...state,
      getFeaturesForAllGatwewaysLoading: false,
    })
  ),
  on(
    InstallationFeaturesActions.fetchFeaturesForInstallationGateways.success,
    (state, { features }): InstallationFeaturesState =>
      featuresAdapter.upsertMany(features, {
        ...state,
        getFeaturesForAllGatwewaysLoading: false,
        getFeaturesRequestFinished: true,
      })
  ),
  on(
    InstallationFeaturesActions.fetchFeaturesForInstallationGateways.error,
    (state): InstallationFeaturesState => ({
      ...state,
      getFeaturesForAllGatwewaysLoading: false,
    })
  ),
  on(
    InstallationFeaturesActions.upsertManyFeatures,
    GatewayActions.FetchFeatureSuccess,
    (state, { features }): InstallationFeaturesState => featuresAdapter.upsertMany(features, state)
  ),
  on(
    InstallationFeaturesActions.removeManyFeatures,
    (state, { featureKeys }): InstallationFeaturesState =>
      featuresAdapter.removeMany(featureKeys, state)
  ),
  on(
    InstallationFeaturesActions.removeAllFeatures,
    (state): InstallationFeaturesState => ({
      ...featuresAdapter.removeAll(state),
      getFeaturesRequestFinished: false,
    })
  ),
  on(
    InstallationFeaturesActions.updateRooms,
    (state, { rooms }): InstallationFeaturesState => ({
      ...state,
      rooms: rooms,
    })
  ),
  on(
    InstallationFeaturesActions.updateAddRoomInfo,
    (state, { room }): InstallationFeaturesState => ({
      ...state,
      addRoom: room,
    })
  ),
  on(
    InstallationFeaturesActions.setReplaceGatewayFormData,
    (state, { formData }): InstallationFeaturesState => ({
      ...state,
      replaceGateWayFormData: formData,
    })
  )
);
